/* eslint-disable no-param-reassign */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { isEmpty, isEqual } from 'lodash';
import { CalibrationPerformanceModel } from 'api/models/CalibrationPerformanceModel';
import { GPC } from 'api/models/GPC';
import { globalAction } from 'common/actions';
import { performanceKeys } from 'common/constants/calibration';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import CalibrationContext from 'context/CalibrationContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { PublicComp } from 'pages/ValuationsAllocation/types';
import { ISO_DATE_FORMAT } from 'utilities';
import { isDateInPeriods, isLastQuarter } from 'utilities/dateUtils';
import {
  CalibrationPerformanceSheetData,
  CalibrationProps,
  CompanyAsOfDatePerformanceMetrics,
  CustomCalibration,
  CustomValuationApproachGPC,
} from './Calibration.props';
import useHandleChangeApproach from './Inputs/useHandleChangeApproach';
import rowConfigCalibrationOutputs from './Outputs/rowConfigCalibrationOutputs';
import { createColumnsCalibrationPerformance, rowConfigCalibrationPerformance } from './performance_metrics/config';
import { RowConfigCalibrationParams } from './performance_metrics/config/rowConfig';
import { PerformanceCalculator } from './utils';
import ValuationContext from '../../../ValuationsAllocation/ValuationContext';
import { useCustomClasses as usePerformanceMetricsCustomClasses } from '../guidelinePublicCompanies/PerformanceMetrics/config';
import { getCompanyPerfMetricsAsOfDate } from '../guidelinePublicCompanies/utils/getCompanyPerfMetricsCalculations';
import { CustomCalibrationAttributes, GuidelinePublicCompaniesAttributesExtended } from '../guidelinePublicCompanies/utils/types';
import useUpdateCustomDatePerformanceMetrics from '../guidelinePublicCompanies/utils/useUpdateCustomDatePerformanceMetrics';
import { getFiscalYearEnd } from '../guidelinePublicCompanies/utils/utilities';

const Calibration = ({
  spreadsheets,
  workbook,
  onChange,
  approaches,
  financials,
  company,
  financialStatementsList,
  calibration,
}: CalibrationProps) => {
  const calibrationInputSheet = spreadsheets.calibration_inputs;
  const calibrationOutputSheet = spreadsheets.calibration_outputs;
  const calibrationPerformanceSheet = spreadsheets.calibration_performance as CalibrationPerformanceSheetData;
  const { customClasses: performanceMetricsCustomClasses } = usePerformanceMetricsCustomClasses();
  const [format, formatDispatch] = useFormat();
  const [compsWithCustomDatePerformanceMetrics, setCompsWithCustomDatePerformanceMetrics] = useState<GPC[]>([]);
  const { tableData: initialTableData } = calibrationInputSheet;
  const { companyName } = initialTableData as { companyName: string };
  const [newDate, setNewDate] = useState<string>();
  const {
    resetConfiguration,
    companyMeasurementDate,
    financialStatementPeriodsPerMD,
    gpcAttributes,
    calibrationAttributes,
  } = useContext(ValuationContext);
  const [globalStore, dispatch] = useStore() as unknown as UseStoreValues;
  const { evaluateSpreadsheets } = workbook;

  // Get the selected approach out of approaches because that is where the updated GPC comparison is stored
  const selectedApproach = useMemo(
    () => approaches.find(approach => approach.panelId === calibration?.approach?.panelId),
    [approaches, calibration?.approach?.panelId]
  );

  // Extract gpcComparison to avoid unnecessary callback re-creations
  const gpcComparison = useMemo(
    () => calibration?.approach?.valuations_approach_gpc?.gpc_comparison,
    [calibration?.approach?.valuations_approach_gpc?.gpc_comparison]
  );

  const { handleChange: updateCalibrationPerformances } = useHandleChangeApproach({
    cell: {} as ICell,
    calibration,
    approaches,
    calibrationPerformanceSheet,
    calibrationOutputSheet,
    company,
    customClasses: performanceMetricsCustomClasses,
    financialStatementsList,
  });

  const { getCompsWithCustomDatePerformanceMetricsCalibration, validateCustomDatePerformanceMetrics }
    = useUpdateCustomDatePerformanceMetrics({
      gpcComparison: (calibration?.approach?.valuations_approach_gpc ?? []) as unknown as PublicComp[],
      format,
      gpcAttributes: gpcAttributes as unknown as GuidelinePublicCompaniesAttributesExtended,
      calibrationAttributes: calibrationAttributes as unknown as CustomCalibrationAttributes,
    });

  const updateApproach = useCallback(
    (approachGpc: CustomValuationApproachGPC, calibration: CustomCalibration) => {
      const asOfDate = newDate;

      const performanceMetricsAsOfDateColumns = createColumnsCalibrationPerformance({
        gpcComparisonByDate: approachGpc.gpc_comparison,
        financials,
      });

      const performanceMetricsAsOfDateCommonRowConfig = {
        asOfDate,
        company,
        customClasses: performanceMetricsCustomClasses,
        gpcComparisonByDate: approachGpc.gpc_comparison,
      };

      const rowConfig = rowConfigCalibrationPerformance({
        ...performanceMetricsAsOfDateCommonRowConfig,
      });

      const performanceMetricsKeys = performanceMetricsAsOfDateColumns.filter(item =>
        Object.keys(item).some(key => key.includes('performance'))
      );

      const numRows = performanceMetricsKeys.length;
      const numCols = Object.keys(performanceMetricsKeys[0]).length;
      calibration.performances = [];

      calibration.performances = PerformanceCalculator({
        numCols,
        numRows,
        performanceMetricsKeys,
        performanceKeys,
        idCalibration: calibration.id!,
        calibration,
      });

      calibrationPerformanceSheet.reset({
        rowConfig,
        columns: performanceMetricsAsOfDateColumns.splice(1),
      });

      resetConfiguration?.();
    },
    [calibrationPerformanceSheet, company, financials, newDate, performanceMetricsCustomClasses, resetConfiguration]
  );

  const updateCustomDatePerformanceMetrics = useCallback(async () => {
    const approachGpc = calibration?.approach?.valuations_approach_gpc ?? {};
    const asOfDate = newDate;
    const companyAsOfDatePerformanceMetrics
      = approachGpc.company_as_of_date_performance_metrics as CompanyAsOfDatePerformanceMetrics[];
    const companyAsOfDatePerformanceMetricsId = companyAsOfDatePerformanceMetrics?.[0]?.id;
    const fiscalYearEnd = getFiscalYearEnd(asOfDate!, companyMeasurementDate);
    if (
      isDateInPeriods(asOfDate!, financialStatementPeriodsPerMD!, 'statement_date', ISO_DATE_FORMAT)
      || isLastQuarter(asOfDate!, fiscalYearEnd)
    ) {
      const { tmpCompanyAsOfDatePerformanceMetrics, rowMustBeReadOnly } = getCompanyPerfMetricsAsOfDate({
        asOfDate,
        financialsPeriods: financialStatementPeriodsPerMD,
        companyName,
        fiscalYearEnd,
      });

      calibration.approach.valuations_approach_gpc = {
        ...calibration.approach.valuations_approach_gpc,
        gpc_comparison: compsWithCustomDatePerformanceMetrics,
        company_as_of_date_performance_metrics: companyAsOfDatePerformanceMetricsId
          ? [{ ...tmpCompanyAsOfDatePerformanceMetrics, id: companyAsOfDatePerformanceMetricsId }]
          : [],
        rowMustBeReadOnly,
      };
    } else {
      calibration.approach.valuations_approach_gpc = {
        ...calibration.approach.valuations_approach_gpc,
        gpc_comparison: compsWithCustomDatePerformanceMetrics,
        rowMustBeReadOnly: false,
        company_as_of_date_performance_metrics: companyAsOfDatePerformanceMetricsId
          ? [{ id: companyAsOfDatePerformanceMetricsId }]
          : [],
      };
    }

    dispatch(globalAction.showLoadingProgress(false));

    updateApproach(calibration.approach.valuations_approach_gpc, calibration);
  }, [
    compsWithCustomDatePerformanceMetrics,
    calibration,
    dispatch,
    newDate,
    companyMeasurementDate,
    financialStatementPeriodsPerMD,
    updateApproach,
    companyName,
  ]);

  const handleAsOfDateAlias = useCallback(
    async value => {
      if (Array.isArray(gpcComparison) && !isEmpty(gpcComparison)) {
        dispatch(globalAction.showLoadingProgress(true));
        const tmpCompsWithCustomDatePerformanceMetrics = (await getCompsWithCustomDatePerformanceMetricsCalibration(
          value,
          gpcComparison as unknown as GPC[]
        )) as CalibrationPerformanceModel[];

        const compsWithValidatedCustomDatePerformanceMetrics = validateCustomDatePerformanceMetrics(
          tmpCompsWithCustomDatePerformanceMetrics
        ) as CalibrationPerformanceModel[];

        const reEnabledPerformanceMetrics = compsWithValidatedCustomDatePerformanceMetrics.map(comp => ({
          ...comp,
          enabled: true,
        })) as GPC[];

        setCompsWithCustomDatePerformanceMetrics(reEnabledPerformanceMetrics);
      }
    },
    [
      gpcComparison,
      dispatch,
      getCompsWithCustomDatePerformanceMetricsCalibration,
      validateCustomDatePerformanceMetrics,
    ]
  );

  const handlePerformanceRowEnabledChange = useCallback(
    async cell => {
      const performanceToUpdate = calibrationPerformanceSheet.tableData.gpcComparisonByDate.find(
        performance =>
          performance.cap_iq_id?.toLowerCase() === cell.rowHeaderId.toLowerCase() || cell.value === performance.name
      );
      if (!performanceToUpdate) {
        return;
      }
      performanceToUpdate.enabled = cell.enabled;
      const columns = {
        valuationsApproachGpc: {} as RowConfigCalibrationParams['valuationsApproachGpc'],
        financials,
        gpcComparisonByDate: calibrationPerformanceSheet.tableData.gpcComparisonByDate,
      };

      const columnsPerformance = createColumnsCalibrationPerformance(columns);
      // to update expressions based on new enabled/disabled rows
      await calibrationOutputSheet.reset({
        rowConfig: rowConfigCalibrationOutputs({
          calibration,
          columnsPerformance,
          isDisabled: false,
        }),
      });
      await calibrationPerformanceSheet.reset({
        rowConfig: rowConfigCalibrationPerformance({
          company,
          gpcComparisonByDate: calibrationPerformanceSheet.tableData.gpcComparisonByDate,
        }),
      });
      await evaluateSpreadsheets([calibrationInputSheet, calibrationPerformanceSheet, calibrationOutputSheet]);
    },
    [
      calibration,
      financials,
      evaluateSpreadsheets,
      company,
      calibrationPerformanceSheet,
      calibrationInputSheet,
      calibrationOutputSheet,
    ]
  );

  const handleOnChange = useCallback(
    async (cell, value) => {
      onChange(cell, value);
      if (cell.alias === 'calibration_date') {
        const currentDate = value;
        setNewDate(currentDate);
        await handleAsOfDateAlias(currentDate);
      } else if (cell.isEditableTitleCell && cell.isComparisonRow) {
        await handlePerformanceRowEnabledChange(cell);
      }
    },
    [handleAsOfDateAlias, onChange, handlePerformanceRowEnabledChange]
  );

  useEffect(() => {
    if (!isEmpty(compsWithCustomDatePerformanceMetrics)) {
      updateCustomDatePerformanceMetrics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compsWithCustomDatePerformanceMetrics]);

  useEffect(() => {
    if (
      calibration
      && selectedApproach
      && updateCalibrationPerformances
      && !isEqual(
        calibration.approach.valuations_approach_gpc?.gpc_comparison,
        selectedApproach?.valuations_approach_gpc?.gpc_comparison
      )
    ) {
      updateCalibrationPerformances();
    }
  }, [calibration, selectedApproach, updateCalibrationPerformances]);

  const GroupContextValue = useMemo(
    () => ({
      approaches,
      calibrationPerformanceSheet,
      calibrationOutputSheet,
      calibrationInputSheet,
      financials,
      company,
      performanceMetricsCustomClasses,
      financialStatementsList,
      calibration,
    }),
    [
      approaches,
      calibrationPerformanceSheet,
      calibrationOutputSheet,
      calibrationInputSheet,
      financials,
      company,
      performanceMetricsCustomClasses,
      financialStatementsList,
      calibration,
    ]
  );

  return (
    <>
      <Box width="100%" display="flex" flexDirection="row">
        <CalibrationContext.Provider value={GroupContextValue}>
          <ScalarSpreadsheet
            {...calibrationInputSheet as unknown as SpreadsheetConfig}
            sheet={calibrationInputSheet}
            workbook={workbook}
            onChange={handleOnChange}
            format={format}
            formatDispatch={formatDispatch}
          />

          <ScalarSpreadsheet
            {...calibrationOutputSheet as unknown as SpreadsheetConfig}
            sheet={calibrationOutputSheet}
            workbook={workbook}
            onChange={handleOnChange}
            format={format}
            formatDispatch={formatDispatch}
          />
        </CalibrationContext.Provider>

        <br />
      </Box>

      <Box width="100%" display="flex" flexDirection="column">
        <ScalarSpreadsheet
          {...calibrationPerformanceSheet as unknown as SpreadsheetConfig}
          sheet={calibrationPerformanceSheet}
          workbook={workbook}
          onChange={handleOnChange}
          format={format}
          formatDispatch={formatDispatch}
        />
      </Box>
    </>
  );
};

export default Calibration;
